import React, { useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import RightSheet from "../../../components/ui-components/RightSheet.jsx";

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

/**
 * UncoveredCostPanel — guided remediation for cost that no source captures.
 *
 * Renders a calm, proportionate warning strip above the matrix when there is uncovered cost,
 * and a RightSheet drawer that breaks that cost down by its biggest contributors (by service)
 * with a one-click "Add source" that deep-links the New-source wizard pre-filled for that
 * service. Renders nothing when everything is covered.
 */
const UncoveredCostPanel = ({ notCoveredCost, totalCost, breakdown, onAddSource, currency = "€" }) => {
  const [open, setOpen] = useState(false);
  // Hide when the uncovered cost rounds to €0 — a sub-currency-unit gap would render the
  // nonsensical "€0 of cost isn't captured" warning, which trains users to ignore the banner.
  // Only a materially visible amount (≥ what displays as €1) is worth flagging.
  if (!notCoveredCost || Math.round(notCoveredCost) < 1) return null;

  const pct = totalCost > 0 ? Math.round((notCoveredCost / totalCost) * 100) : 0;
  const items = breakdown || [];

  return (
    <>
      {/* Warning strip — amber/info, not alarm: a small gap (here ~2%) shouldn't shout. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Sizes to its content — the caller keeps it compact and right-aligns the page action
        // beside it. (w-full is a no-op here anyway, so width is content-driven.)
        className="inline-flex items-center gap-3 rounded-card border border-warning bg-warning-soft px-4 py-3 text-left hover:bg-warning-soft transition-colors"
      >
        <FontAwesomeIcon
          icon={icon({ name: "triangle-exclamation", family: "sharp", style: "solid" })}
          className="text-warning flex-shrink-0"
        />
        <span className="text-sm text-text">
          <span className="font-semibold text-text">{euro(notCoveredCost)}</span>
          {pct > 0 && <span className="text-text-2"> ({pct}%)</span>} of cost isn&rsquo;t captured by any source.
        </span>
        <span className="ml-auto flex items-center gap-1 text-sm font-medium text-primary flex-shrink-0">
          Review
          <FontAwesomeIcon icon={icon({ name: "arrow-right", family: "sharp", style: "regular" })} className="text-xs" />
        </span>
      </button>

      <RightSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Cost not covered by a source"
        subtitle={`${euro(notCoveredCost)}${pct > 0 ? ` · ${pct}% of total` : ""} — add a source to capture it`}
      >
        <p className="text-sm text-text-2 mb-4">
          These services have cost that no source matches, so it can&rsquo;t be owned or allocated. Add a source
          for each to bring it into the matrix.
        </p>
        {items.length === 0 ? (
          <p className="text-sm text-text-2">
            Breakdown unavailable. Add a catch-all source (or run an allocation) to capture the residual {currency}
            {Math.round(notCoveredCost).toLocaleString()}.
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => {
              const share = notCoveredCost > 0 ? Math.round((it.cost / notCoveredCost) * 100) : 0;
              return (
                <li
                  key={it.service}
                  className="flex items-center gap-3 rounded-ctrl border border-border px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-text truncate" title={it.service}>{it.service}</div>
                    <div className="text-xs text-text-2">
                      {euro(it.cost)}{share > 0 ? ` · ${share}% of uncovered` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onAddSource(it.service)}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-ctrl bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover transition-colors"
                  >
                    <FontAwesomeIcon icon={icon({ name: "plus", family: "sharp", style: "regular" })} className="text-xs" />
                    Add source
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </RightSheet>
    </>
  );
};

UncoveredCostPanel.propTypes = {
  notCoveredCost: PropTypes.number,
  totalCost: PropTypes.number,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({ service: PropTypes.string, cost: PropTypes.number })
  ),
  onAddSource: PropTypes.func.isRequired,
  currency: PropTypes.string,
};

export default UncoveredCostPanel;
